import { useState, useEffect } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"
import { useQuery } from "@apollo/client"
import { TablePageLayout } from "~/layouts/table-page-layout"
import { Form } from "~/ui/form"
import { Button } from "~/ui/button"
import { TextField } from "~/fields/text-field"
import { DateTimePicker } from "~/fields/date-time-picker"
import { Checkbox } from "~/ui/checkbox"
import { Label } from "~/ui/label"
import { useSafeMutation } from "~/common/use-safe-mutation"
import { useValidationErrors } from "~/common/use-validation-errors"
import { useToast } from "~/ui/use-toast"
import { GAME_GENRES, GAME_INTENSITIES } from "~/types/game-preferences"
import { gql } from "~/__generated__"
import { GameGenre, GameIntensity } from "~/__generated__/graphql"
import { GraphqlError } from "~/ui/errors"

const GET_PLAN_QUERY = gql(/* GraphQL */ `
  query GetPlanForEdit($id: ID!) {
    node(id: $id) {
      __typename
      ... on Plan {
        id
        title
        description
        datetime
        minPeople
        maxPeople
        genrePreferences
        intensityPreferences
        location {
          id
          name
        }
      }
    }
  }
`)

const UPDATE_PLAN_MUTATION = gql(/* GraphQL */ `
  mutation UpdatePlan($input: PlanUpdateInput!) {
    planUpdate(input: $input) {
      plan {
        id
        title
        description
        datetime
        minPeople
        maxPeople
        genrePreferences
        intensityPreferences
      }
    }
  }
`)

const formSchema = z.object({
  title: z.string().min(1, "Title is required"),
  description: z.string().optional(),
  datetime: z.date(),
  minPeople: z.preprocess(
    (val) => (val === "" ? undefined : Number(val)),
    z.number().min(1).optional()
  ),
  maxPeople: z.preprocess(
    (val) => (val === "" ? undefined : Number(val)),
    z.number().min(1).optional()
  ),
  genrePreferences: z.array(z.string()),
  intensityPreferences: z.array(z.string()),
})

type FormValues = z.infer<typeof formSchema>

export const PlanEditScreen = () => {
  const { toast } = useToast()
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const [selectedGenres, setSelectedGenres] = useState<string[]>([])
  const [selectedIntensities, setSelectedIntensities] = useState<string[]>([])

  const {
    data,
    loading: queryLoading,
    error,
  } = useQuery(GET_PLAN_QUERY, {
    variables: { id: id || "" },
    skip: !id,
  })

  const plan = data?.node?.__typename === "Plan" ? data.node : null

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      title: "",
      description: "",
      datetime: new Date(),
      minPeople: undefined,
      maxPeople: undefined,
      genrePreferences: [],
      intensityPreferences: [],
    },
  })

  const [updatePlan, mutationResult] = useSafeMutation(UPDATE_PLAN_MUTATION)
  useValidationErrors(form.setError, mutationResult)

  // Set form values when plan data is loaded
  useEffect(() => {
    if (plan && !queryLoading) {
      form.reset({
        title: plan.title,
        description: plan.description || "",
        datetime: new Date(plan.datetime),
        minPeople: plan.minPeople || undefined,
        maxPeople: plan.maxPeople || undefined,
        genrePreferences: [],
        intensityPreferences: [],
      })

      // Set selected genres and intensities
      if (plan.genrePreferences && plan.genrePreferences.length > 0) {
        setSelectedGenres(plan.genrePreferences)
      } else {
        setSelectedGenres([])
      }

      if (plan.intensityPreferences && plan.intensityPreferences.length > 0) {
        setSelectedIntensities(plan.intensityPreferences)
      } else {
        setSelectedIntensities([])
      }
    }
  }, [plan, queryLoading, form])

  const onSubmit = async (values: FormValues) => {
    if (!id) return

    console.log("Starting plan update submission with values:", {
      ...values,
      genrePreferences: selectedGenres,
      intensityPreferences: selectedIntensities,
    })

    try {
      console.log("Sending mutation with variables:", {
        input: {
          id,
          title: values.title,
          description: values.description || "",
          datetime: values.datetime.toISOString(),
          minPeople: values.minPeople,
          maxPeople: values.maxPeople,
          genrePreferences: selectedGenres,
          intensityPreferences: selectedIntensities,
        },
      })

      const result = await updatePlan({
        variables: {
          input: {
            id,
            title: values.title,
            description: values.description || "",
            datetime: values.datetime.toISOString(),
            minPeople: values.minPeople,
            maxPeople: values.maxPeople,
            genrePreferences: selectedGenres.filter((g) => g !== "any") as GameGenre[],
            intensityPreferences: selectedIntensities.filter((i) => i !== "any") as GameIntensity[],
          },
        },
      })

      console.log("Mutation result:", result)

      if (result.data?.planUpdate?.plan) {
        console.log("Plan updated successfully:", result.data.planUpdate.plan)
        toast({
          title: "Plan Updated",
          description: "Your plan has been successfully updated.",
        })
        navigate(`/plans/${id}`)
      } else {
        console.error("Plan update returned no data:", result)
      }
    } catch (error) {
      console.error("Error updating plan:", error)
      // Log more details about the error
      if (error instanceof Error) {
        console.error("Error message:", error.message)
        console.error("Error stack:", error.stack)
      }

      // If it's a GraphQL error with extensions, log those too
      if (error && typeof error === "object" && "graphQLErrors" in error) {
        console.error("GraphQL errors:", error.graphQLErrors)
      }
    }
  }

  const handleGenreChange = (genreId: string, checked: boolean) => {
    if (genreId === "any") {
      if (checked) {
        // Select all genres
        const allGenres = Object.values(GAME_GENRES).map((genre) => genre.id)
        setSelectedGenres(["any", ...allGenres])
      } else {
        // Deselect all genres
        setSelectedGenres([])
      }
    } else {
      if (checked) {
        setSelectedGenres([...selectedGenres.filter((id) => id !== "any"), genreId])
      } else {
        // Remove "any" if it was selected
        const newGenres = selectedGenres.filter((id) => id !== genreId && id !== "any")
        setSelectedGenres(newGenres)
      }
    }
  }

  const handleIntensityChange = (intensityId: string, checked: boolean) => {
    if (intensityId === "any") {
      if (checked) {
        // Select all intensities
        const allIntensities = Object.values(GAME_INTENSITIES).map((intensity) => intensity.id)
        setSelectedIntensities(["any", ...allIntensities])
      } else {
        // Deselect all intensities
        setSelectedIntensities([])
      }
    } else {
      if (checked) {
        setSelectedIntensities([...selectedIntensities.filter((id) => id !== "any"), intensityId])
      } else {
        // Remove "any" if it was selected
        const newIntensities = selectedIntensities.filter(
          (id) => id !== intensityId && id !== "any"
        )
        setSelectedIntensities(newIntensities)
      }
    }
  }

  if (queryLoading) {
    return (
      <TablePageLayout>
        <div className="flex items-center justify-center p-8">
          <p>Loading plan details...</p>
        </div>
      </TablePageLayout>
    )
  }

  if (error) {
    return <GraphqlError error={error} />
  }

  if (!plan) {
    return (
      <TablePageLayout>
        <div className="flex items-center justify-center p-8">
          <p>Plan not found</p>
        </div>
      </TablePageLayout>
    )
  }

  return (
    <TablePageLayout>
      <div className="mx-auto max-w-2xl space-y-6">
        <div>
          <h1 className="text-2xl font-bold">Edit Plan</h1>
          <p className="text-gray-600">Update your game plan details</p>
        </div>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <div className="space-y-4">
              <div>
                <Label>Location</Label>
                <div className="rounded-md border border-input bg-background px-3 py-2 text-sm text-muted-foreground">
                  {plan.location.name} (cannot be changed)
                </div>
              </div>

              <DateTimePicker control={form.control} name="datetime" label="Date & Time" required />

              <div className="grid grid-cols-2 gap-4">
                <TextField
                  control={form.control}
                  name="minPeople"
                  label="Min People"
                  type="number"
                  min={1}
                />
                <TextField
                  control={form.control}
                  name="maxPeople"
                  label="Max People"
                  type="number"
                  min={1}
                />
              </div>

              <TextField
                control={form.control}
                name="title"
                label="Title"
                placeholder="Enter a title for your plan"
                required
              />

              <TextField
                control={form.control}
                name="description"
                label="Description"
                placeholder="Describe your plan (optional)"
                multiline
                rows={3}
              />
            </div>

            <div className="mt-6 space-y-4">
              <div>
                <h3 className="mb-2 text-lg font-medium">Game Genres</h3>
                <div className="grid grid-cols-2 gap-2">
                  <div
                    key="any-genre"
                    className="col-span-2 flex items-center space-x-2 border-b border-gray-200 pb-2"
                  >
                    <Checkbox
                      id="genre-any"
                      checked={selectedGenres.includes("any")}
                      onCheckedChange={(checked) => handleGenreChange("any", checked as boolean)}
                    />
                    <Label htmlFor="genre-any" className="text-sm font-medium">
                      Any Genre (Select All)
                    </Label>
                  </div>
                  {Object.values(GAME_GENRES).map((genre) => (
                    <div key={genre.id} className="flex items-center space-x-2">
                      <Checkbox
                        id={`genre-${genre.id}`}
                        checked={selectedGenres.includes(genre.id)}
                        onCheckedChange={(checked) =>
                          handleGenreChange(genre.id, checked as boolean)
                        }
                      />
                      <Label htmlFor={`genre-${genre.id}`} className="text-sm">
                        {genre.label}
                      </Label>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <h3 className="mb-2 text-lg font-medium">Game Intensity</h3>
                <div className="grid grid-cols-2 gap-2">
                  <div
                    key="any-intensity"
                    className="col-span-2 flex items-center space-x-2 border-b border-gray-200 pb-2"
                  >
                    <Checkbox
                      id="intensity-any"
                      checked={selectedIntensities.includes("any")}
                      onCheckedChange={(checked) =>
                        handleIntensityChange("any", checked as boolean)
                      }
                    />
                    <Label htmlFor="intensity-any" className="text-sm font-medium">
                      Any Intensity (Select All)
                    </Label>
                  </div>
                  {Object.values(GAME_INTENSITIES).map((intensity) => (
                    <div key={intensity.id} className="flex items-center space-x-2">
                      <Checkbox
                        id={`intensity-${intensity.id}`}
                        checked={selectedIntensities.includes(intensity.id)}
                        onCheckedChange={(checked) =>
                          handleIntensityChange(intensity.id, checked as boolean)
                        }
                      />
                      <Label htmlFor={`intensity-${intensity.id}`} className="text-sm">
                        {intensity.label}
                      </Label>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex items-center gap-4 pt-4">
              <Button type="submit" disabled={form.formState.isSubmitting}>
                Update Plan
              </Button>
              <Button type="button" variant="outline" onClick={() => navigate(`/plans/${id}`)}>
                Cancel
              </Button>
            </div>
          </form>
        </Form>
      </div>
    </TablePageLayout>
  )
}
